<template>
  <div
    v-for="button in workspaceButtons"
    :key="button.icon"
    class="workspace-switcher__btn"
    :class="[{ sm: isScreenSmall }, button.classList]"
  >
    <RouterLink
      v-if="button.to"
      v-slot="{ isActive }"
      :to="button.to"
    >
      <MainNavButton
        :label="button.label"
        :icon="button.icon"
        :active="isActive"
        :name="button.icon"
      />
    </RouterLink>
    <MainNavButton
      v-else
      v-loading="button.isLoading ?? false"
      :label="button.label"
      :icon="button.icon"
      :name="button.icon"
      :active="false"
      :disabled="button.isLoading"
      @click="button.navigateFn"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import { useAsyncState } from '@vueuse/core'
import { vLoading } from 'element-plus'
import { noop } from 'lodash-es'
import MainNavButton from '@/ContextTab/components/MainNav/MainNavButton.vue'
import { useAccess } from '@/ContextTab/stores/auth/useAccess'
import { ACCESS } from '@/lib/accessCodes'
import { usePsRoute } from '@/ContextTab/composables/psRoute'
import { useScreenBreakpoints } from '@/ContextTab/composables/useScreenBreakpoints'
import { legacyBridge } from '@/services/legacyBridge'
import { ROUTE_MAP } from '@/router'
import { useNewsEditorStore } from '@/stores/newsEditor.ts'
import { ENewsTypeReverse } from '@/lib/references.ts'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile.ts'
import { ENABLE_NEW_EDITOR } from '@/config'
import { NotificationHandlerKey } from '@/injectionKeys.ts'


type WorkspaceButton = {
  label: string
  to: RouteLocationRaw
  navigateFn?: undefined
  isLoading?: boolean
  icon: string
  visible: boolean
  classList?: string
} | {
  label: string
  to?: undefined
  navigateFn: () => void
  isLoading?: boolean
  icon: string
  visible: boolean
  classList?: string
}

const { onError } = inject(NotificationHandlerKey, { onError: noop })

const { isLegacyRoute } = usePsRoute()
const { isScreenSmall } = useScreenBreakpoints()
const { userHasAllPermissions } = useAccess()

const newsEditorStore = useNewsEditorStore()
const profileStore = useUserProfileStore()

const workspaceButtons = computed<WorkspaceButton[]>(() =>
  ([
    {
      label: 'Опубликованные',
      to: {
        name: ROUTE_MAP.published,
      },
      icon: 'published',
      visible: !isLegacyRoute.value,
    },
    {
      label: 'Редактор',
      classList: 'editor-theme-sandy',
      to: {
        name: ROUTE_MAP.newsEditorById,
        params: {
          id: newsEditorStore.newsItem?.id,
        },
      },
      icon: 'pencil',
      visible: !isLegacyRoute.value && ENABLE_NEW_EDITOR,
    },
    {
      label: 'Холсты',
      navigateFn: () => legacyBridge.openCanvas(window.contextId),
      icon: 'canvas',
      visible: isLegacyRoute.value && userHasAllPermissions([ACCESS.NEWS.CANVAS]),
    },
    {
      label: 'Отложенные',
      to: {
        name: ROUTE_MAP.embargo,
      },
      icon: 'embargo',
      visible: false,
    },
    {
      label: 'Поиск',
      to: {
        name: ROUTE_MAP.search,
      },
      icon: 'search',
      visible: false,
    },
    {
      label: 'Создать молнию',
      classList: 'editor-theme-sandy',
      navigateFn: () => createFlash(0, ENewsTypeReverse.FLASH),
      isLoading: isLoadingCreateFlash.value,
      icon: 'zap',
      visible: !isLegacyRoute.value && ENABLE_NEW_EDITOR && userHasAllPermissions([ACCESS.NEWS.CREATION]),
    },
    {
      label: 'Создать новость',
      classList: 'editor-theme-sandy',
      navigateFn: () => createNews(0, profileStore.profile.default_news_type_id ?? ENewsTypeReverse.NEWS),
      isLoading: isLoadingCreateNews.value,
      icon: 'plus',
      visible: !isLegacyRoute.value && ENABLE_NEW_EDITOR && userHasAllPermissions([ACCESS.NEWS.CREATION]),
    },
  ] satisfies WorkspaceButton[]).filter((button) => button.visible),
)

const { create: createPromise, openByNewsItem } = useNewsEditorStore()

const { isLoading: isLoadingCreateFlash, execute: createFlash } = useAsyncState(
  createPromise,
  undefined,
  { onError, onSuccess: openByNewsItem, immediate: false },
)

const { isLoading: isLoadingCreateNews, execute: createNews } = useAsyncState(
  createPromise,
  undefined,
  { onError, onSuccess: openByNewsItem, immediate: false },
)
</script>

<style scoped>
.workspace-switcher__btn {
  position: relative;

  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 80%;
    height: 1px;
    left: 10%;
    top: 0;
    background: var(--ps-text-color);
  }

  &.sm:before {
    width: 1px;
    height: 70%;
    left: 0;
    top: 15%;
  }
}
</style>
